import { useRef, useState } from "react";
import api, { formatApiError, productSummary } from "@/lib/api";
import { toast } from "sonner";
import { History } from "lucide-react";

export const FAB_UNIDAD = [
  { value: "pendiente", label: "Pendiente", cls: "bg-zinc-200 text-zinc-800" },
  { value: "pendiente_material", label: "Pendiente material", cls: "bg-orange-100 text-orange-900" },
  { value: "en_proceso", label: "En proceso", cls: "bg-amber-100 text-amber-900" },
  { value: "solicitar_base", label: "Solicitar base", cls: "bg-yellow-200 text-yellow-900" },
  { value: "listo", label: "Fabricado", cls: "bg-emerald-100 text-emerald-900" },
];
const LBL = Object.fromEntries(FAB_UNIDAD.map((x) => [x.value, x]));
const gtDT = (ts) => (ts ? new Date(ts).toLocaleString("es-GT", { timeZone: "America/Guatemala", dateStyle: "short", timeStyle: "short" }) : "");

/** Unidades derivadas en el navegador con la misma regla del servidor (_fab_unidades). */
export const unidadesDe = (o) => {
  const g = o.fab_unidades || {};
  const out = [];
  (o.items || []).forEach((it, i) => {
    const key = `${i}:${it.product_id || ""}`;
    const qty = it.cantidad || 0;
    const hechas = o.estado_fabricacion === "listo" ? qty : (o.fab_parcial || {})[key] || 0;
    for (let u = 1; u <= qty; u++) {
      const uk = `${key}#${u}`;
      const x = g[uk] || {};
      out.push({ ukey: uk, it, unidad: u, de: qty, estado: x.estado || (u <= hechas ? "listo" : "pendiente"), at: x.at, by: x.by, historial: x.historial || [], base: x.base });
    }
  });
  return out;
};

const leer = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });

const Unidad = ({ o, u, ultima, onChanged }) => {
  const [hist, setHist] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const enviar = async (estado, foto_base64 = "") => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/fabricacion/unidad/${o.id}`, { ukey: u.ukey, estado, foto_base64 });
      toast.success(estado === "solicitar_base" ? "Unidad enviada a Bases" : data.estado_pedido === "listo" ? `${o.numero} fabricado completo` : "Estado de la unidad actualizado");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const cambiar = (estado) => {
    if (estado === "listo" && ultima) { fileRef.current.dataset.estado = estado; return fileRef.current.click(); }
    enviar(estado);
  };
  const e = LBL[u.estado] || LBL.pendiente;
  return (
    <li className="border border-zinc-200 rounded-md p-2 bg-white" data-testid={`fab-unidad-${o.numero}-${u.ukey}`}>
      <div className="flex items-start gap-2">
        <span className="font-mono text-[10px] font-bold bg-zinc-900 text-white rounded px-1.5 py-0.5 whitespace-nowrap">{o.numero} · {u.unidad}/{u.de}</span>
        <span className="flex-1 text-xs font-medium uppercase tracking-tight leading-snug">{productSummary(u.it)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <select value={u.estado} disabled={busy} onChange={(ev) => cambiar(ev.target.value)} className={`text-xs font-semibold rounded px-1.5 py-1 border-0 ${e.cls}`} data-testid={`fab-unidad-estado-${o.numero}-${u.ukey}`}>
          {FAB_UNIDAD.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </select>
        {u.base && <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-900" data-testid={`fab-unidad-base-${o.numero}-${u.ukey}`}>Base {u.base.numero}: {u.base.estado}</span>}
        {u.historial.length > 0 && <button type="button" onClick={() => setHist(!hist)} className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5" data-testid={`fab-unidad-hist-btn-${o.numero}-${u.ukey}`}><History className="w-3 h-3" /> {u.historial.length}</button>}
        {u.by && <span className="text-[10px] text-zinc-400">{u.by} · {gtDT(u.at)}</span>}
      </div>
      {hist && <ul className="mt-1 text-[10px] text-zinc-600 space-y-0.5" data-testid={`fab-unidad-hist-${o.numero}-${u.ukey}`}>{u.historial.map((h, i) => <li key={i}>{gtDT(h.at)} · {h.by}: {LBL[h.de]?.label || h.de} → {LBL[h.estado]?.label || h.estado}</li>)}</ul>}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={async (ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (f) enviar("listo", await leer(f)); }} />
    </li>
  );
};

export const FabUnidades = ({ o, onChanged }) => {
  const us = unidadesDe(o);
  const pend = us.filter((u) => u.estado !== "listo").length;
  return (
    <ul className="space-y-1.5" data-testid={`fab-unidades-${o.numero}`}>
      {us.map((u) => <Unidad key={u.ukey} o={o} u={u} ultima={pend === 1 && u.estado !== "listo"} onChanged={onChanged} />)}
    </ul>
  );
};
